import { Resource, MedicationStatement, Medication } from '@medplum/fhirtypes'
import { formatDateTime } from '@/utils'
import { JSX, useEffect, useState } from 'react'
import { useMedplum } from '@medplum/react'

type Props = {
  resource?: Resource
}

type Combined = MedicationStatement & { _medication: Medication }

const availFields = [
  { code: 'medication', title: 'Medication', width: '15%' },
  { code: 'instructions', title: 'Instructions', width: '25%' },
  { code: 'quantity', title: 'Quantity' },
  { code: 'status', title: 'Status' },
  { code: 'effective', title: 'Started' },
  { code: 'stopped', title: 'Stopped' },
  { code: 'indication', title: 'Indication' },
  { code: 'doctor', title: 'Doctor' },
]

const renderColumn = (
  resourceSource: MedicationStatement,
  itemCode: string,
): string | JSX.Element => {
  const resource = resourceSource as Combined

  switch (itemCode) {
    case 'medication':
      return (
        resource._medication?.code?.text ||
        resource._medication?.code?.coding?.[0].display ||
        resource.medicationCodeableConcept?.text ||
        resource.medicationCodeableConcept?.coding?.[0]?.display ||
        resource.medicationCodeableConcept?.coding?.[0]?.code ||
        resource.contained?.find((item) => item.resourceType === 'Medication')?.code?.text ||
        '-'
      )
    case 'instructions': {
      // `dosage[0].text` is the canonical narrative ("10 mg PO daily")
      // stamped by `toMedicationStatement` on cascade-created records;
      // `additionalInstruction[0].text` is the legacy slot some older
      // resources use; `note[0].text` is the doctor's free-text note from
      // the Plan section's Note column.
      const dosageText = resource.dosage?.[0]?.text
      const additional = resource.dosage?.[0]?.additionalInstruction?.[0]?.text
      const note = resource.note?.[0]?.text
      const lines = [dosageText, additional, note].filter(Boolean)
      if (lines.length === 0) return ''
      return (
        <div>
          {lines.map((line, idx) => (
            <div key={idx}>{line}</div>
          ))}
        </div>
      )
    }
    case 'quantity': {
      const dose = resource.dosage?.[0]?.doseAndRate?.[0]?.doseQuantity
      const unit = dose?.unit ? ` ${dose.unit}` : ''
      if (dose?.value !== undefined) return `${dose.value}${unit}`

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      const dose2 = resource.dosage?.[0]?.doseQuantity
      const unit2 = dose2?.unit ? ` ${dose2.unit}` : ''
      if (dose2?.value !== undefined) return `${dose2.value}${unit2}`
      return ''
    }
    case 'status':
      return resource.status || '-'
    case 'effective': {
      if (resource.effectiveDateTime) return formatDateTime(resource.effectiveDateTime)
      if (resource.effectivePeriod?.start) return formatDateTime(resource.effectivePeriod.start)
      if (resource.dateAsserted) return formatDateTime(resource.dateAsserted)
      return ''
    }
    case 'stopped':
      return resource.effectivePeriod?.end ? formatDateTime(resource.effectivePeriod.end) : ''
    case 'indication':
      return (
        <div>
          {(resource.reasonCode || []).map((r, idx) => (
            <div key={idx}>{r.text || r.coding?.[0]?.display || '-'}</div>
          ))}
        </div>
      )
    case 'doctor':
      return resource.informationSource?.display || ''
    default:
      return ''
  }
}

export function Medications({ resource }: Props) {
  const [medication, setMedication] = useState<MedicationStatement>()
  const medplum = useMedplum()

  useEffect(() => {
    if (!resource) return
    medplum
      .search('MedicationStatement', `_id=${resource.id}&_include=MedicationStatement:medication`)
      .then((res) => {
        const list = (res.entry?.map((e) => e.resource) || []) as (
          | MedicationStatement
          | Medication
        )[]
        const medications = list.filter((item) => item.resourceType === 'Medication')

        const stmt = resource as MedicationStatement
        const medication = stmt.medicationReference?.reference
        if (medication) {
          const medId = medication.split('/').pop()
          const medRes = medications.find((m) => m.id === medId)
          if (medRes) {
            ;(stmt as Combined)._medication = medRes
          }
        }
        setMedication(stmt)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource])

  if (!medication) {
    return null
  }

  return (
    <div className="border-b-1 border-b-gray-200 p-2">
      <table className="text-base">
        <tbody>
          {availFields.map((item) => (
            <tr key={item.code}>
              <td className="py-1 text-sm font-bold text-[#7D7987]">{item.title}:</td>
              <td className="px-[8px] py-1 text-base text-sm">
                <div>{renderColumn(medication, item.code)}</div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
